/**
 * Typed API error + a single response guard, so retry/permission decisions key on the
 * HTTP status instead of substring-matching hand-written error strings (audit finding 180).
 */

export class ApiError extends Error {
  readonly status: number;
  readonly detail?: unknown;

  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Throw a typed {@link ApiError} when a fetch Response is not ok.
 *
 * Reads the body once, preferring a JSON `detail` field (FastAPI's error shape) for the
 * message and falling back to `fallbackMessage` or the status text. No-op on ok responses,
 * so call sites can do `await assertOk(response); return response.json();`.
 */
/**
 * Normalise FastAPI's error `detail` to a readable string, or null when no
 * readable message exists so callers fall back to their own.
 *
 * `detail` is a string for HTTPExceptions but a LIST of `{loc, msg, type}`
 * objects for 422 validation errors; `new Error(detail)` on the list renders
 * the literal toast text "[object Object]" (#5865).
 */
export function extractApiErrorDetail(detail: unknown): string | null {
  if (typeof detail === 'string') return detail.length > 0 ? detail : null;
  if (Array.isArray(detail)) {
    const messages = detail
      .map(item =>
        typeof item === 'string' ? item : typeof (item as { msg?: unknown })?.msg === 'string' ? (item as { msg: string }).msg : null,
      )
      .filter((m): m is string => m !== null && m.length > 0);
    return messages.length > 0 ? messages.join('; ') : null;
  }
  if (detail !== null && typeof detail === 'object') {
    const candidates = detail as { msg?: unknown; detail?: unknown; message?: unknown };
    for (const value of [candidates.msg, candidates.detail, candidates.message]) {
      if (typeof value === 'string' && value.length > 0) return value;
    }
  }
  return null;
}

export async function assertOk(response: Response, fallbackMessage?: string): Promise<void> {
  if (response.ok) return;

  let detail: unknown;
  let message = fallbackMessage ?? `Request failed: ${response.status} ${response.statusText}`;

  try {
    const body = await response.clone().json();
    detail = body?.detail;
    const readable = extractApiErrorDetail(detail);
    if (readable !== null) {
      message = readable;
    } else if (detail != null) {
      message = JSON.stringify(detail); // last resort: debuggable, never [object Object]
    }
  } catch {
    // Non-JSON body — keep the fallback/status message.
  }

  throw new ApiError(response.status, message, detail);
}
